// game/weapons.ts — WeaponForm registry and the forms themselves.
// One equipped per run; shared body, different verbs (DESIGN §2.1).
// Adding Storm Staff / Bulwark Maul is one edit inside this file: write the form,
// call registerWeapon. Nothing outside weapons.ts needs to know they exist.

import type { ProjectileDef } from '../types'
import { TUNING } from '../data/tuning'
import * as Input from '../engine/input'
import { registerProjectile, type EnemyEnt } from './entities'
import { registerRecipe } from '../engine/sprites'
import { emit } from '../engine/particles'
import { fx } from '../engine/fx'
import type { Run } from './game'

export interface WeaponForm {
  id: string
  name: string
  /** movement multiplier while equipped */
  moveMul: number
  /** true = hold LMB to auto-repeat */
  auto: boolean
  /** advertised cooldowns — the HUD reads these, the forms set the real timers */
  primaryCooldown: number
  specialCooldown: number
  /** the RMB special's name + one plain line of what it does (pause menu, D-029) */
  specialName: string
  specialDesc: string
  /** multiplier on damage the Warden takes while this form is equipped */
  damageTakenMul?: number
  primary(run: Run, ax: number, ay: number): void
  special(run: Run, ax: number, ay: number): void
  /** Optional per-step hook for forms that hold state between inputs. */
  tick?(run: Run, dt: number): void
  /**
   * Optional contact guard, asked by the touch-damage pass before a body's
   * touch lands. Return true to stop the touch; the form owns any feedback
   * (pushback, sparks). The pass keeps checking other bodies — a guard that
   * holds the front does not pardon what is chewing on your back.
   */
  blockContact?(run: Run, e: EnemyEnt): boolean
  /** Optional world-space visual, drawn over the Warden. */
  render?(ctx: CanvasRenderingContext2D, px: number, py: number): void
}

const WEAPONS = new Map<string, WeaponForm>()

export function registerWeapon(w: WeaponForm): void {
  WEAPONS.set(w.id, w)
}

export function getWeapon(id: string): WeaponForm {
  return WEAPONS.get(id) ?? WEAPONS.get('cinderblade') ?? FALLBACK
}

export function weaponIds(): string[] {
  return [...WEAPONS.keys()]
}

const FALLBACK: WeaponForm = {
  id: 'none',
  name: 'Bare Hands',
  moveMul: 1,
  auto: true,
  primaryCooldown: 0.4,
  specialCooldown: 1,
  specialName: 'Nothing',
  specialDesc: 'Nothing happens.',
  primary() {},
  special() {},
}

// --------------------------------------------------------------- helpers

function angleDiff(a: number, b: number): number {
  let d = a - b
  while (d > Math.PI) d -= Math.PI * 2
  while (d < -Math.PI) d += Math.PI * 2
  return d
}

/** True when a body of radius `r` at (x,y) sits inside the arc (dir, arc, range). */
function inArc(
  x: number,
  y: number,
  r: number,
  cx: number,
  cy: number,
  dir: number,
  arc: number,
  range: number,
): boolean {
  const dx = x - cx
  const dy = y - cy
  const d = Math.hypot(dx, dy)
  if (d > range + r) return false
  if (d <= 4) return true
  // The angular test must include the body's own width: a fat target (a boss
  // head) can overlap the swing's wedge while its CENTRE sits outside it, and
  // testing the centre alone makes frontal-feeling blows whiff (D-032). Widen
  // the wedge by the target's angular half-width at this distance.
  const half = arc / 2 + Math.asin(Math.min(1, r / d))
  return Math.abs(angleDiff(Math.atan2(dy, dx), dir)) <= half
}

/**
 * One melee sweep: cleaves every enemy in the arc, chews generators, and lands
 * on a boss body. Floor food is safe from melee — "shot the food" belongs to
 * shots (D-010). `stagger` (Bulwark Maul) stuns what it hits for that many
 * seconds; enemies.ts reads `tags.stunT`.
 */
export function meleeSweep(
  run: Run,
  cx: number,
  cy: number,
  dir: number,
  arc: number,
  range: number,
  damage: number,
  knockback: number,
  stagger = 0,
): number {
  let hits = 0
  const list = run.enemyPool.live
  for (let i = list.length - 1; i >= 0; i--) {
    const e = list[i]
    if (!e.alive) continue
    if (!inArc(e.pos.x, e.pos.y, e.radius, cx, cy, dir, arc, range)) continue
    run.damageEnemy(e, damage, cx, cy, knockback)
    if (stagger > 0 && e.alive) {
      const cur = typeof e.tags.stunT === 'number' ? e.tags.stunT : 0
      e.tags.stunT = Math.max(cur, stagger)
    }
    hits++
  }
  for (let i = 0; i < run.generators.length; i++) {
    const g = run.generators[i]
    if (!g.alive) continue
    if (!inArc(g.pos.x, g.pos.y, g.radius, cx, cy, dir, arc, range)) continue
    run.damageGenerator(g, damage)
    hits++
  }
  const b = run.boss
  if (b && b.alive && inArc(b.pos.x, b.pos.y, b.radius, cx, cy, dir, arc, range)) {
    run.damageBoss(damage)
    hits++
  }
  return hits
}

// --------------------------------------------------------------- projectiles
// Weapon projectiles are ProjectileDefs like any other; the readability law
// applies to the player too (straight = white-gold).

const ARROW: ProjectileDef = {
  id: 'wp_arrow',
  kind: 'straight',
  speed: TUNING.weapons.whisperbow.speed,
  damage: TUNING.weapons.whisperbow.damage,
  radius: 4,
  color: '#ffe9a8',
  lifetime: 1.4,
}
registerProjectile(ARROW)
registerRecipe(ARROW.id, { key: 'arrow', size: 15, params: { color: ARROW.color } })

// --------------------------------------------------------------- Cinder Blade

registerWeapon({
  id: 'cinderblade',
  name: 'Cinder Blade',
  moveMul: 1,
  auto: true,
  primaryCooldown: TUNING.weapons.cinderblade.cooldown,
  specialCooldown: TUNING.weapons.cinderblade.special.cooldown,
  specialName: 'Rekindle Lunge',
  specialDesc: 'Dash-strike through enemies, leaving a burning trail behind you.',
  primary(run, ax, ay) {
    const t = TUNING.weapons.cinderblade
    const p = run.player
    const step = p.combo % 3
    const heavy = step === 2
    const arc = t.swingArc * (heavy ? 1.3 : 1)
    const range = t.range * (heavy ? 1.16 : 1)
    const dmg = t.damage * (heavy ? 1.45 : 1)
    const dir = Math.atan2(ay, ax)

    p.swingT = 0.18
    p.swingDur = 0.18
    p.swingDir = dir
    p.swingArc = arc
    p.swingRange = range
    p.swingSign = step === 1 ? -1 : 1
    p.swingHeavy = heavy

    meleeSweep(run, p.pos.x, p.pos.y, dir, arc, range, dmg, heavy ? 210 : 110)
    p.combo = (p.combo + 1) % 3
    p.comboTimer = 0.8
    p.primaryCd = t.cooldown * (heavy ? 1.4 : 1)
    run.sfx('swing')
    if (heavy) fx.shake(2, 0.12)

    emit(p.pos.x + ax * range * 0.6, p.pos.y + ay * range * 0.6, {
      count: heavy ? 9 : 5,
      color: heavy ? '#ffb347' : '#ffe9c0',
      speed: 130,
      life: 0.2,
      size: 3,
      angle: dir,
      spread: arc,
      shape: 1,
    })
  },
  special(run, ax, ay) {
    const s = TUNING.weapons.cinderblade.special
    const p = run.player
    p.startLunge(run, ax, ay, s.dashLen, s.damage, s.trailSeconds, s.trailDps)
    p.specialCd = s.cooldown
    run.bark('Rekindle.')
  },
})

// --------------------------------------------------------------- Whisper Bow

registerWeapon({
  id: 'whisperbow',
  name: 'Whisper Bow',
  moveMul: TUNING.weapons.whisperbow.moveMul,
  auto: true,
  primaryCooldown: TUNING.weapons.whisperbow.cooldown,
  specialCooldown: TUNING.weapons.whisperbow.special.cooldown,
  specialName: 'Fan Volley',
  specialDesc: 'Five arrows in a fan, knocking enemies back.',
  primary(run, ax, ay) {
    const t = TUNING.weapons.whisperbow
    const p = run.player
    const pr = run.spawnProjectileEx('wp_arrow', p.pos.x + ax * 12, p.pos.y + ay * 12, ax, ay, true)
    if (pr) {
      pr.damage = t.damage
      pr.pierce = t.pierce
    }
    p.primaryCd = t.cooldown
    p.muzzle = 0.07
    run.sfx('shoot')
    emit(p.pos.x + ax * 16, p.pos.y + ay * 16, {
      count: 3,
      color: '#fff3c4',
      speed: 120,
      life: 0.12,
      size: 2,
      angle: Math.atan2(ay, ax),
      spread: 0.5,
      shape: 1,
    })
  },
  special(run, ax, ay) {
    const s = TUNING.weapons.whisperbow.special
    const t = TUNING.weapons.whisperbow
    const p = run.player
    const base = Math.atan2(ay, ax)
    const spread = (s.spreadDeg * Math.PI) / 180
    const denom = Math.max(1, s.count - 1)
    for (let i = 0; i < s.count; i++) {
      const f = i / denom - 0.5
      const a = base + f * spread
      const dx = Math.cos(a)
      const dy = Math.sin(a)
      const pr = run.spawnProjectileEx('wp_arrow', p.pos.x + dx * 12, p.pos.y + dy * 12, dx, dy, true)
      if (pr) {
        pr.damage = t.damage
        pr.pierce = t.pierce
        pr.knockback = s.knockback
      }
    }
    p.specialCd = s.cooldown
    p.muzzle = 0.12
    run.sfx('shoot')
    fx.shake(2.5, 0.14)
    emit(p.pos.x + ax * 16, p.pos.y + ay * 16, {
      count: 10,
      color: '#fff3c4',
      speed: 170,
      life: 0.2,
      size: 3,
      angle: base,
      spread,
      shape: 1,
    })
    run.bark('Loose.')
  },
})

// --------------------------------------------------------------- Storm Staff
// "Screen control, worst single-target, generator-melter" (DESIGN §2.1).
// The bolt is slow and fat and bursts where it stops: the burst is what melts a
// generator and what clears a knot of swarm — and, per D-010, what ruins the
// ration you were saving. Splash is applied by the run (game.ts friendlySplash)
// so every way a bolt can end pays out exactly once.

const STORMBOLT: ProjectileDef = {
  id: 'wp_stormbolt',
  kind: 'straight',
  speed: TUNING.weapons.stormstaff.speed,
  damage: TUNING.weapons.stormstaff.damage,
  radius: 6,
  // The readability law colours *enemy* fire by behaviour; the Warden's own fire
  // must read as the Warden's. Storm-blue is unmistakably yours.
  color: '#8fd8ff',
  lifetime: 1.7,
}
registerProjectile(STORMBOLT)
registerRecipe(STORMBOLT.id, { key: 'stormbolt', size: 16, params: { color: STORMBOLT.color } })

/**
 * Stormring: a nova that grows out of the Warden over growSeconds. Every body is
 * damaged once, on the frame the ring reaches it, and enemy projectiles inside
 * the ring are deleted — the same rule Run.emberburst applies for the potion
 * (game.ts: "if (!p.alive || p.friendly) continue; ... p.alive = false"), spread
 * over the expansion instead of fired in one frame.
 */
const nova = {
  run: null as Run | null,
  active: false,
  t: 0,
  x: 0,
  y: 0,
  radius: 0,
  grow: 0.5,
  damage: 0,
  hit: [] as EnemyEnt[],
  hitGens: [] as number[],
  hitBoss: false,
}

function novaTick(run: Run, dt: number): void {
  if (!nova.active) return
  nova.t += dt
  const k = Math.min(1, nova.t / nova.grow)
  const r = nova.radius * k
  const list = run.enemyPool.live
  for (let i = list.length - 1; i >= 0; i--) {
    const e = list[i]
    if (!e.alive) continue
    if (Math.hypot(e.pos.x - nova.x, e.pos.y - nova.y) > r + e.radius) continue
    if (nova.hit.indexOf(e) >= 0) continue
    nova.hit.push(e)
    run.damageEnemy(e, nova.damage, nova.x, nova.y, 180)
  }
  for (let i = 0; i < run.generators.length; i++) {
    const g = run.generators[i]
    if (!g.alive || nova.hitGens.indexOf(i) >= 0) continue
    if (Math.hypot(g.pos.x - nova.x, g.pos.y - nova.y) > r + g.radius) continue
    nova.hitGens.push(i)
    run.damageGenerator(g, nova.damage)
  }
  if (!nova.hitBoss && run.hitBoss(nova.x, nova.y, r, nova.damage)) nova.hitBoss = true
  const pr = run.projectilePool.live
  for (let i = pr.length - 1; i >= 0; i--) {
    const p = pr[i]
    if (!p.alive || p.friendly) continue
    if (Math.hypot(p.pos.x - nova.x, p.pos.y - nova.y) > r) continue
    p.alive = false
    emit(p.pos.x, p.pos.y, { count: 4, color: '#8fd8ff', speed: 90, life: 0.2, size: 2, shape: 1 })
  }
  if (nova.t >= nova.grow) {
    nova.active = false
    nova.hit.length = 0
    nova.hitGens.length = 0
  }
}

registerWeapon({
  id: 'stormstaff',
  name: 'Storm Staff',
  moveMul: 1,
  auto: true,
  primaryCooldown: TUNING.weapons.stormstaff.cooldown,
  specialCooldown: TUNING.weapons.stormstaff.special.cooldown,
  specialName: 'Stormring',
  specialDesc: 'An expanding nova that damages everything and destroys enemy shots.',
  primary(run, ax, ay) {
    const t = TUNING.weapons.stormstaff
    const p = run.player
    const pr = run.spawnProjectileEx('wp_stormbolt', p.pos.x + ax * 14, p.pos.y + ay * 14, ax, ay, true)
    if (pr) {
      pr.damage = t.damage
      pr.splash = t.splashRadius
      pr.splashDamage = t.splash
    }
    p.primaryCd = t.cooldown
    p.muzzle = 0.1
    run.sfx('shoot')
    emit(p.pos.x + ax * 16, p.pos.y + ay * 16, {
      count: 5,
      color: '#bfeaff',
      speed: 110,
      life: 0.16,
      size: 3,
      angle: Math.atan2(ay, ax),
      spread: 0.7,
      shape: 1,
    })
  },
  special(run, _ax, _ay) {
    const s = TUNING.weapons.stormstaff.special
    const p = run.player
    nova.run = run
    nova.active = true
    nova.t = 0
    nova.x = p.pos.x
    nova.y = p.pos.y
    nova.radius = s.radius
    nova.grow = s.growSeconds
    nova.damage = s.damage
    nova.hit.length = 0
    nova.hitGens.length = 0
    nova.hitBoss = false
    p.specialCd = s.cooldown
    run.sfx('potion')
    fx.shake(4, 0.25)
    emit(p.pos.x, p.pos.y, {
      count: 26,
      color: '#8fd8ff',
      speed: 260,
      speedVar: 140,
      life: 0.4,
      lifeVar: 0.2,
      size: 3,
      drag: 0.9,
      shape: 1,
    })
    run.bark('Stormring.')
  },
  tick(run, dt) {
    if (nova.run !== run) {
      // a new run inherited the module: drop stale state rather than replay it
      nova.run = run
      nova.active = false
    }
    novaTick(run, dt)
  },
  render(ctx, _px, _py) {
    if (!nova.active) return
    const k = Math.min(1, nova.t / nova.grow)
    const r = nova.radius * k
    ctx.save()
    ctx.globalAlpha = 0.5 * (1 - k * 0.7)
    ctx.strokeStyle = '#bfeaff'
    ctx.lineWidth = 6
    ctx.beginPath()
    ctx.arc(nova.x, nova.y, r, 0, Math.PI * 2)
    ctx.stroke()
    ctx.globalAlpha = 0.22 * (1 - k)
    ctx.strokeStyle = '#8fd8ff'
    ctx.lineWidth = 16
    ctx.stroke()
    ctx.globalAlpha = 0.1 * (1 - k)
    ctx.fillStyle = '#5fbfff'
    ctx.beginPath()
    ctx.arc(nova.x, nova.y, r, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  },
})

// -------------------------------------------------------------- Bulwark Maul
// "Tanky rhythm play; only form with a block" (DESIGN §2.1, reworked D-030).
// The slam is wide and slow, and it staggers — and a staggered body crumples,
// taking bonus damage from everything (game.ts damageEnemy), so the slam is the
// setup and the next blow is the payoff. Warden's Wall is the only defensive
// verb in the game: hold RMB to keep the shield raised — it stops frontal
// contact and returns every frontal shot as a steel bolt — then release (or hit
// the cap) to shove. The wall converts the enemy's range into yours: the rooms
// that punish melee are the rooms that feed it.

const wall = {
  run: null as Run | null,
  active: false,
  /** seconds held so far — counts up toward the blockSeconds cap */
  t: 0,
  dir: 0,
  /** throttle for contact-block feedback, so a grinding body sparks, not strobes */
  fxT: 0,
}

/** Reach of the raised shield, and of the shove that ends it. */
const WALL_BLOCK_RADIUS = 46
const WALL_ARC = 2.0
const SHOVE_RANGE = 78
const SHOVE_ARC = 2.2

// The returned shot. Steel-white so it reads as the Warden's (readability law);
// straight regardless of what flew in — a shield face has one answer.
const REFLECT: ProjectileDef = {
  id: 'wp_reflect',
  kind: 'straight',
  speed: 440,
  damage: TUNING.weapons.bulwarkmaul.special.reflectDamage,
  radius: 4,
  color: '#dfe6f4',
  lifetime: 1.2,
}
registerProjectile(REFLECT)
registerRecipe(REFLECT.id, { key: 'arrow', size: 15, params: { color: REFLECT.color } })

function wallBlock(run: Run, dir: number): number {
  const p = run.player
  let blocked = 0
  const pr = run.projectilePool.live
  for (let i = pr.length - 1; i >= 0; i--) {
    const q = pr[i]
    if (!q.alive || q.friendly) continue
    if (!inArc(q.pos.x, q.pos.y, q.radius, p.pos.x, p.pos.y, dir, WALL_ARC, WALL_BLOCK_RADIUS)) continue
    q.alive = false
    blocked++
    // every blocked shot returns along its own line; a parked or degenerate
    // velocity goes back out along the shield's facing instead
    let rx = -q.vel.x
    let ry = -q.vel.y
    const l = Math.hypot(rx, ry)
    if (l < 0.001) {
      rx = Math.cos(dir)
      ry = Math.sin(dir)
    } else {
      rx /= l
      ry /= l
    }
    const bolt = run.spawnProjectileEx(REFLECT.id, q.pos.x, q.pos.y, rx, ry, true)
    if (bolt) bolt.damage = TUNING.weapons.bulwarkmaul.special.reflectDamage
    run.sfx('hit')
    emit(q.pos.x, q.pos.y, {
      count: 7,
      color: '#cfd6e4',
      speed: 150,
      life: 0.25,
      size: 3,
      angle: dir + Math.PI,
      spread: 1.4,
    })
  }
  return blocked
}

function shove(run: Run, dir: number): void {
  const s = TUNING.weapons.bulwarkmaul.special
  const p = run.player
  // the shove clears the lane in front of it, projectiles included
  wallBlock(run, dir)
  const list = run.enemyPool.live
  for (let i = list.length - 1; i >= 0; i--) {
    const e = list[i]
    if (!e.alive) continue
    if (!inArc(e.pos.x, e.pos.y, e.radius, p.pos.x, p.pos.y, dir, SHOVE_ARC, SHOVE_RANGE)) continue
    run.damageEnemy(e, s.shoveDamage, p.pos.x, p.pos.y, s.shoveForce)
    if (e.alive) {
      const cur = typeof e.tags.stunT === 'number' ? e.tags.stunT : 0
      e.tags.stunT = Math.max(cur, TUNING.weapons.bulwarkmaul.stagger)
    }
  }
  for (let i = 0; i < run.generators.length; i++) {
    const g = run.generators[i]
    if (!g.alive) continue
    if (!inArc(g.pos.x, g.pos.y, g.radius, p.pos.x, p.pos.y, dir, SHOVE_ARC, SHOVE_RANGE)) continue
    run.damageGenerator(g, s.shoveDamage)
  }
  const b = run.boss
  if (b && b.alive && inArc(b.pos.x, b.pos.y, b.radius, p.pos.x, p.pos.y, dir, SHOVE_ARC, SHOVE_RANGE)) {
    run.damageBoss(s.shoveDamage)
  }
  run.sfx('swing')
  fx.shake(5, 0.22)
  emit(p.pos.x + Math.cos(dir) * 26, p.pos.y + Math.sin(dir) * 26, {
    count: 16,
    color: '#cfd6e4',
    speed: 240,
    speedVar: 120,
    life: 0.3,
    lifeVar: 0.15,
    size: 4,
    angle: dir,
    spread: SHOVE_ARC,
  })
  run.bark('Off me.')
}

registerWeapon({
  id: 'bulwarkmaul',
  name: 'Bulwark Maul',
  moveMul: 1,
  auto: true,
  primaryCooldown: TUNING.weapons.bulwarkmaul.cooldown,
  specialCooldown: TUNING.weapons.bulwarkmaul.special.cooldown,
  specialName: "Warden's Wall",
  specialDesc: 'Hold to raise your shield — it stops frontal blows and returns frontal shots. Release to shove everything back.',
  damageTakenMul: TUNING.weapons.bulwarkmaul.damageTakenMul,
  primary(run, ax, ay) {
    const t = TUNING.weapons.bulwarkmaul
    const p = run.player
    const dir = Math.atan2(ay, ax)

    p.swingT = 0.26
    p.swingDur = 0.26
    p.swingDir = dir
    p.swingArc = t.swingArc
    p.swingRange = t.range
    p.swingSign = 1
    p.swingHeavy = true

    meleeSweep(run, p.pos.x, p.pos.y, dir, t.swingArc, t.range, t.damage, 190, t.stagger)
    p.primaryCd = t.cooldown
    run.sfx('swing')
    fx.shake(3.5, 0.16)
    fx.hitstop(TUNING.fx.hitstopHit)
    emit(p.pos.x + ax * t.range * 0.55, p.pos.y + ay * t.range * 0.55, {
      count: 10,
      color: '#d7dce8',
      speed: 150,
      life: 0.24,
      size: 4,
      angle: dir,
      spread: t.swingArc,
    })
  },
  special(run, ax, ay) {
    // Raising the wall is free; the cooldown starts when the shove ends it.
    // player.ts only calls this off a fresh press with specialCd spent, so the
    // only re-entry to guard is a press landing while the wall is already up.
    if (wall.active && wall.run === run) return
    wall.run = run
    wall.active = true
    wall.t = 0
    wall.dir = Math.atan2(ay, ax)
    run.sfx('ui')
    run.bark('Wall.')
  },
  tick(run, dt) {
    if (wall.run !== run) {
      wall.run = run
      wall.active = false
    }
    if (wall.fxT > 0) wall.fxT -= dt
    if (!wall.active) return
    // The shield follows the aim while it is up: angling it is the skill.
    wall.dir = Math.atan2(run.player.aim.y, run.player.aim.x)
    wallBlock(run, wall.dir)
    wall.t += dt
    const s = TUNING.weapons.bulwarkmaul.special
    // Held as long as RMB is held, up to the cap; the release IS the shove,
    // so the player picks the moment the lane gets cleared.
    if (!Input.isDown('special') || wall.t >= s.blockSeconds) {
      wall.active = false
      shove(run, wall.dir)
      run.player.specialCd = s.cooldown
    }
  },
  blockContact(run, e) {
    if (!wall.active || wall.run !== run) return false
    const p = run.player
    if (!inArc(e.pos.x, e.pos.y, e.radius, p.pos.x, p.pos.y, wall.dir, WALL_ARC, WALL_BLOCK_RADIUS + p.radius)) {
      return false
    }
    // held, not hurt: the body grinds off the shield face
    if (!e.tags.noKnockback) {
      const dx = e.pos.x - p.pos.x
      const dy = e.pos.y - p.pos.y
      const l = Math.hypot(dx, dy) || 1
      e.kbx += (dx / l) * 240
      e.kby += (dy / l) * 240
    }
    if (wall.fxT <= 0) {
      wall.fxT = 0.2
      run.sfx('hit')
      emit(p.pos.x + Math.cos(wall.dir) * (p.radius + 8), p.pos.y + Math.sin(wall.dir) * (p.radius + 8), {
        count: 6,
        color: '#cfd6e4',
        speed: 140,
        life: 0.22,
        size: 3,
        angle: wall.dir,
        spread: 1.2,
      })
    }
    return true
  },
  render(ctx, px, py) {
    if (!wall.active) return
    const k = Math.min(1, wall.t / TUNING.weapons.bulwarkmaul.special.blockSeconds)
    const a0 = wall.dir - WALL_ARC / 2
    const a1 = wall.dir + WALL_ARC / 2
    ctx.save()
    ctx.globalAlpha = 0.75
    ctx.strokeStyle = '#dfe6f4'
    ctx.lineWidth = 7
    ctx.lineCap = 'butt'
    ctx.beginPath()
    ctx.arc(px, py, 26, a0, a1)
    ctx.stroke()
    ctx.globalAlpha = 0.22 + 0.18 * Math.sin(k * 22)
    ctx.strokeStyle = '#8fb6ff'
    ctx.lineWidth = 15
    ctx.stroke()
    ctx.restore()
  },
})
